import type { ResponsiveLayout, GridColumn } from "./blocks";

/**
 * Maps a grid column value to responsive Tailwind classes for each breakpoint
 */
const responsiveColSpanClasses: Record<"sm" | "md" | "lg" | "xl", Record<GridColumn, string>> = {
  sm: {
    1: "sm:col-span-1",
    2: "sm:col-span-2",
    3: "sm:col-span-3",
    4: "sm:col-span-4",
    5: "sm:col-span-5",
    6: "sm:col-span-6",
    7: "sm:col-span-7",
    8: "sm:col-span-8",
    9: "sm:col-span-9",
    10: "sm:col-span-10",
    11: "sm:col-span-11",
    12: "sm:col-span-12",
  },
  md: {
    1: "md:col-span-1",
    2: "md:col-span-2",
    3: "md:col-span-3",
    4: "md:col-span-4",
    5: "md:col-span-5",
    6: "md:col-span-6",
    7: "md:col-span-7",
    8: "md:col-span-8",
    9: "md:col-span-9",
    10: "md:col-span-10",
    11: "md:col-span-11",
    12: "md:col-span-12",
  },
  lg: {
    1: "lg:col-span-1",
    2: "lg:col-span-2",
    3: "lg:col-span-3",
    4: "lg:col-span-4",
    5: "lg:col-span-5",
    6: "lg:col-span-6",
    7: "lg:col-span-7",
    8: "lg:col-span-8",
    9: "lg:col-span-9",
    10: "lg:col-span-10",
    11: "lg:col-span-11",
    12: "lg:col-span-12",
  },
  xl: {
    1: "xl:col-span-1",
    2: "xl:col-span-2",
    3: "xl:col-span-3",
    4: "xl:col-span-4",
    5: "xl:col-span-5",
    6: "xl:col-span-6",
    7: "xl:col-span-7",
    8: "xl:col-span-8",
    9: "xl:col-span-9",
    10: "xl:col-span-10",
    11: "xl:col-span-11",
    12: "xl:col-span-12",
  },
};

/**
 * Converts a ResponsiveLayout object to Tailwind CSS classes for grid column spans.
 * Uses mobile-first approach where xs is the base and larger breakpoints override.
 *
 * @param layout - The responsive layout configuration
 * @returns Space-separated Tailwind classes for column spans
 *
 * @example
 * getLayoutClasses({ xs: 12, md: 6, lg: 4 })
 * // Returns: "col-span-12 md:col-span-6 lg:col-span-4"
 */
export function getLayoutClasses(layout?: ResponsiveLayout): string {
  if (!layout) {
    // Default to full width
    return "col-span-12";
  }

  const classes: string[] = [];

  // Mobile (xs) is always full-width to prevent smushed content on small screens
  classes.push("col-span-12");

  // If xs was set to less than 12, promote it to the sm breakpoint
  // so the configured width takes effect on tablet-sized screens (≥ 640px)
  const xsValue = layout.xs ?? 12;
  if (xsValue < 12 && layout.sm === undefined) {
    classes.push(responsiveColSpanClasses.sm[xsValue]);
  }

  // Responsive breakpoints - only include if defined
  if (layout.sm !== undefined) {
    classes.push(responsiveColSpanClasses.sm[layout.sm]);
  }
  if (layout.md !== undefined) {
    classes.push(responsiveColSpanClasses.md[layout.md]);
  }
  if (layout.lg !== undefined) {
    classes.push(responsiveColSpanClasses.lg[layout.lg]);
  }
  if (layout.xl !== undefined) {
    classes.push(responsiveColSpanClasses.xl[layout.xl]);
  }

  return classes.join(" ");
}

/**
 * CSS classes for the grid container that wraps blocks.
 * Uses a 12-column grid with gap.
 */
export const GRID_CONTAINER_CLASSES = "grid grid-cols-12 gap-4";

/**
 * Maps a grid column value to an explicit Tailwind width fraction (n/12), per breakpoint.
 * Unlike col-span-*, these are plain `width` values, so they apply the same way regardless
 * of whether the parent is a grid (top-level blocks) or a flex container (blocks nested in
 * a Section) — needed for elements like Button that are inline-sized by default and would
 * otherwise be stretched full-width by a flex parent's default `align-items: stretch`.
 */
const responsiveWidthFractionClasses: Record<"base" | "sm" | "md" | "lg" | "xl", Record<GridColumn, string>> = {
  base: {
    1: "w-1/12",
    2: "w-2/12",
    3: "w-3/12",
    4: "w-4/12",
    5: "w-5/12",
    6: "w-6/12",
    7: "w-7/12",
    8: "w-8/12",
    9: "w-9/12",
    10: "w-10/12",
    11: "w-11/12",
    12: "w-full",
  },
  sm: {
    1: "sm:w-1/12",
    2: "sm:w-2/12",
    3: "sm:w-3/12",
    4: "sm:w-4/12",
    5: "sm:w-5/12",
    6: "sm:w-6/12",
    7: "sm:w-7/12",
    8: "sm:w-8/12",
    9: "sm:w-9/12",
    10: "sm:w-10/12",
    11: "sm:w-11/12",
    12: "sm:w-full",
  },
  md: {
    1: "md:w-1/12",
    2: "md:w-2/12",
    3: "md:w-3/12",
    4: "md:w-4/12",
    5: "md:w-5/12",
    6: "md:w-6/12",
    7: "md:w-7/12",
    8: "md:w-8/12",
    9: "md:w-9/12",
    10: "md:w-10/12",
    11: "md:w-11/12",
    12: "md:w-full",
  },
  lg: {
    1: "lg:w-1/12",
    2: "lg:w-2/12",
    3: "lg:w-3/12",
    4: "lg:w-4/12",
    5: "lg:w-5/12",
    6: "lg:w-6/12",
    7: "lg:w-7/12",
    8: "lg:w-8/12",
    9: "lg:w-9/12",
    10: "lg:w-10/12",
    11: "lg:w-11/12",
    12: "lg:w-full",
  },
  xl: {
    1: "xl:w-1/12",
    2: "xl:w-2/12",
    3: "xl:w-3/12",
    4: "xl:w-4/12",
    5: "xl:w-5/12",
    6: "xl:w-6/12",
    7: "xl:w-7/12",
    8: "xl:w-8/12",
    9: "xl:w-9/12",
    10: "xl:w-10/12",
    11: "xl:w-11/12",
    12: "xl:w-full",
  },
};

/**
 * Converts a ResponsiveLayout into explicit Tailwind width classes (as opposed to
 * getLayoutClasses' grid col-span classes). Use this for elements that size to their
 * content by default (e.g. a Button) and need to keep the same configured width whether
 * they're a top-level block or nested inside a Section's flex column.
 *
 * Returns "w-auto" when no layout is configured, preserving natural content width.
 */
export function getResponsiveWidthClasses(layout?: ResponsiveLayout): string {
  if (!layout) {
    return "w-auto";
  }

  const classes: string[] = [];

  const xsValue = layout.xs ?? 12;
  classes.push(responsiveWidthFractionClasses.base[xsValue]);

  if (xsValue < 12 && layout.sm === undefined) {
    classes.push(responsiveWidthFractionClasses.sm[xsValue]);
  }

  if (layout.sm !== undefined) {
    classes.push(responsiveWidthFractionClasses.sm[layout.sm]);
  }
  if (layout.md !== undefined) {
    classes.push(responsiveWidthFractionClasses.md[layout.md]);
  }
  if (layout.lg !== undefined) {
    classes.push(responsiveWidthFractionClasses.lg[layout.lg]);
  }
  if (layout.xl !== undefined) {
    classes.push(responsiveWidthFractionClasses.xl[layout.xl]);
  }

  return classes.join(" ");
}
